import React from 'react';
import { PathSet, Position } from '../../types';
import { PieceKind } from '../../utils/path';
import { yellow } from '../colors';

export type LiveTrail = {
  kind: PieceKind;
  points: Position[];
};

// SVG overlay drawing recorded movement paths over the pool/court. Shared by
// both PoolElement implementations: the sport- and view-specific pool-metre ->
// screen-pixel mapping is injected via toScreen (must return the PIECE CENTRE
// for the given kind, matching the piece divs' own placement math).
// Two layers: the piece's live drag trail (bright) and the active snapshot's
// saved paths (dim). Pointer events pass through; z-index sits between the
// court markings (<=25) and the pieces (50).
export const PathOverlay: React.FC<{
  width: number;
  height: number;
  strokeWidth: number;
  toScreen: (pos: Position, kind: PieceKind) => { x: number; y: number };
  liveTrail?: LiveTrail | null;
  savedPaths?: PathSet | null;
}> = ({ width, height, strokeWidth, toScreen, liveTrail, savedPaths }) => {
  const toPolylinePoints = (points: Position[], kind: PieceKind): string =>
    points
      .map((pos) => {
        const screen = toScreen(pos, kind);
        return `${screen.x},${screen.y}`;
      })
      .join(' ');

  const savedPolylines: { key: string; points: string }[] = [];
  if (savedPaths) {
    (['players', 'opponents', 'goalkeepers'] as const).forEach((kind) => {
      savedPaths[kind]?.forEach((piecePath, index) => {
        if (piecePath && piecePath.points.length > 1) {
          savedPolylines.push({
            key: `${kind}-${index}`,
            points: toPolylinePoints(piecePath.points, kind),
          });
        }
      });
    });
    if (savedPaths.ball && savedPaths.ball.points.length > 1) {
      savedPolylines.push({
        key: 'ball',
        points: toPolylinePoints(savedPaths.ball.points, 'ball'),
      });
    }
  }

  if (savedPolylines.length === 0 && !liveTrail) {
    return null;
  }

  return (
    <svg
      width={width}
      height={height}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        zIndex: 40,
        pointerEvents: 'none',
      }}
    >
      {savedPolylines.map(({ key, points }) => (
        <polyline
          key={key}
          points={points}
          fill="none"
          stroke={yellow}
          strokeWidth={strokeWidth}
          strokeDasharray={`${strokeWidth * 2} ${strokeWidth * 2}`}
          strokeLinecap="round"
          strokeLinejoin="round"
          opacity={0.35}
        />
      ))}
      {liveTrail && liveTrail.points.length > 1 && (
        <polyline
          points={toPolylinePoints(liveTrail.points, liveTrail.kind)}
          fill="none"
          stroke={yellow}
          strokeWidth={strokeWidth}
          strokeDasharray={`${strokeWidth * 2} ${strokeWidth * 2}`}
          strokeLinecap="round"
          strokeLinejoin="round"
          opacity={0.9}
        />
      )}
    </svg>
  );
};
